import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, ArrowLeft, Clapperboard } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-[#050505] text-white lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden overflow-hidden border-r border-white/10 lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -left-20 top-1/4 h-[420px] w-[420px] rounded-full bg-amber-500/15 blur-[150px]" />
          <div className="absolute bottom-0 right-0 h-[300px] w-[300px] rounded-full bg-orange-600/10 blur-[130px]" />
        </div>
        <Link to="/" className="font-['Outfit'] text-xl font-black tracking-tight text-white">
          Listing<span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">to</span>Video
        </Link>
        <div className="max-w-md">
          <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400/20 to-orange-500/10 text-amber-400 ring-1 ring-inset ring-amber-400/20">
            <Clapperboard className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <h1 className="font-['Outfit'] text-5xl font-black leading-[1.03] tracking-tighter text-white">
            The{" "}
            <span className="font-['Playfair_Display'] font-normal italic text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-500">studio</span>{" "}
            behind the reels.
          </h1>
          <p className="mt-5 font-['DM_Sans'] text-base leading-relaxed text-zinc-400">
            Paste a link, direct the shots, render the film. Your private control room.
          </p>
        </div>
        <p className="font-['DM_Sans'] text-xs text-zinc-600">© {new Date().getFullYear()} Listing to Video</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 font-['DM_Sans'] sm:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <Link to="/" className="mb-10 inline-flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-amber-400">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>

          <p className="font-['DM_Sans'] text-xs font-bold uppercase tracking-[0.25em] text-amber-400">Studio access</p>
          <h2 className="mt-3 mb-9 font-['Outfit'] text-4xl font-black tracking-tight text-white">
            Welcome back.
          </h2>

          <div className="space-y-5">
            <div>
              <label className="text-[11px] font-medium uppercase tracking-[0.15em] text-zinc-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-amber-400/60 focus:ring-2 focus:ring-amber-400/20"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium uppercase tracking-[0.15em] text-zinc-500">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-amber-400/60 focus:ring-2 focus:ring-amber-400/20"
              />
            </div>
          </div>

          {error && (
            <p data-testid="login-error" className="mt-4 text-sm text-orange-400">
              {error}
            </p>
          )}

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="group mt-9 h-12 w-full rounded-full border-0 bg-gradient-to-r from-amber-400 to-orange-500 font-['DM_Sans'] text-base font-bold text-[#0a0500] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(245,166,35,0.4)]"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <span className="flex items-center gap-2">
                Enter Studio
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
